import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FileText, ShieldCheck, Scale, Landmark } from "lucide-react";

import MainLayout from "@/components/MainLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { publicApi } from "@/lib/api";

const keyBenefits = [
  {
    title: "Geprüfte Außenwirkung",
    copy: "Die Mitgliedschaft ermöglicht die Führung der Kennung im geordneten Verbandsrahmen der Vereinigung.",
    icon: ShieldCheck,
  },
  {
    title: "Sichtbarkeit und Einordnung",
    copy: "Mitglieder werden innerhalb der Experten-Suche und des öffentlichen Auftritts erkennbar qualitätsbezogen eingeordnet.",
    icon: Scale,
  },
  {
    title: "Weiterbildung und Verbandsanbindung",
    copy: "Mitgliedschaft, Fortbildungsrahmen, Verfahren und Verbandskommunikation folgen einem dokumentierten Qualitätsverständnis.",
    icon: Landmark,
  },
];

export default function LegalPage({ session, onLogout }) {
  const [content, setContent] = useState(null);

  useEffect(() => {
    const load = async () => {
      const data = await publicApi.getContent();
      setContent(data);
    };
    load();
  }, []);

  const legalDocs = useMemo(() => content?.legal || [], [content]);

  return (
    <MainLayout
      session={session}
      onLogout={onLogout}
      pageTitle="Rechtsgrundlagen und Ordnungen"
      pageDescription="Rechtsgrundlagen, Satzung, Ordnungen und Hinweise der Vereinigung REEXPERTS."
    >
      <div className="bg-[#F8F9FA] text-slate-900">
        <div className="mx-auto flex max-w-7xl flex-col gap-12 px-6 py-14 lg:px-8 lg:py-20">
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="grid gap-8 xl:grid-cols-[0.95fr_1.05fr]" data-testid="legal-hero-section">
            <div className="space-y-6 rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
              <Badge className="w-fit rounded-full border border-slate-200 bg-slate-100 text-slate-700" data-testid="legal-hero-badge">
                <span className="mr-3 inline-flex items-center gap-1">
                  <span className="h-2 w-4 rounded-full bg-[#2f2f2f]" />
                  <span className="h-2 w-4 rounded-full bg-[#a8171b]" />
                  <span className="h-2 w-4 rounded-full bg-[#d4af37]" />
                </span>
                Rechtliche Grundlagen und Mitgliedschaftsordnung
              </Badge>
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#8a6f3b]" data-testid="legal-hero-kicker">Satzung · Hinweise · Klauseln · Verbandsvorteile</p>
                <h1 className="mt-4 font-serif text-[1.95rem] leading-[1.04] text-slate-900 sm:text-4xl lg:text-5xl" data-testid="legal-hero-heading">
                  Rechtsgrundlagen und Ordnungen von REEXPERTS.
                </h1>
              </div>
              <p className="text-base leading-relaxed text-slate-700" data-testid="legal-hero-description">
                Hier finden sich die maßgeblichen Grundlagen der Vereinigung, insbesondere Satzung, Mitgliedschaft, Datenschutz, Nutzungsordnung, Klauseln und Hinweise zur Beschwerdestelle. Die Inhalte werden in geordneter Form ausgewiesen und bleiben innerhalb der Verwaltungsoberfläche editierbar.
              </p>
              <p className="text-sm uppercase tracking-[0.22em] text-[#8a6f3b]" data-testid="legal-hero-statute-note">
                Die Satzung ist öffentlich vollständig einsehbar und zugleich intern editierbar.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/apply" data-testid="legal-hero-apply-link">
                  <Button className="rounded-full bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]">Mitgliedschaft beantragen</Button>
                </Link>
                <Link to="/verfahrensordnung" data-testid="legal-hero-procedure-link">
                  <Button variant="outline" className="rounded-full border-slate-300 bg-white text-slate-900 hover:bg-slate-50">Zur Verfahrensordnung</Button>
                </Link>
                <Link to="/beitragsordnung" data-testid="legal-hero-contribution-link">
                  <Button variant="outline" className="rounded-full border-slate-300 bg-white text-slate-900 hover:bg-slate-50">Zur Beitragsordnung</Button>
                </Link>
                <Link to="/beschwerde" data-testid="legal-hero-complaint-link">
                  <Button variant="outline" className="rounded-full border-slate-300 bg-white text-slate-900 hover:bg-slate-50">Beschwerdestelle öffnen</Button>
                </Link>
              </div>
            </div>

            <div className="grid gap-5">
              {keyBenefits.map((item) => {
                const Icon = item.icon;
                return (
                  <Card key={item.title} className="border-slate-200 bg-white shadow-sm" data-testid={`legal-benefit-card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <CardContent className="space-y-4 p-6">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-900">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{item.title}</p>
                        <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.copy}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </motion.section>

          <section className="grid gap-8 xl:grid-cols-[260px_1fr]" data-testid="legal-content-section">
            <aside className="rounded-[2rem] border border-white/10 bg-[#151515] p-6" data-testid="legal-sidebar">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#b99d6b]" data-testid="legal-sidebar-kicker">Inhaltsübersicht</p>
              <div className="mt-5 space-y-2">
                {legalDocs.map((doc) => (
                  <a
                    key={doc.slug}
                    href={`#${doc.slug}`}
                    className="block rounded-2xl border border-white/10 px-4 py-3 text-sm text-[#e8e1d5] transition-colors hover:border-[#d4af37]/30 hover:bg-[#e5d3b3]/10"
                    data-testid={`legal-sidebar-link-${doc.slug}`}
                  >
                    {doc.title}
                  </a>
                ))}
              </div>
            </aside>

            <div className="space-y-5">
              {legalDocs.map((doc) => (
                <Card key={doc.slug} id={doc.slug} className="scroll-mt-28 border-white/10 bg-[#171717]" data-testid={`legal-document-card-${doc.slug}`}>
                  <CardContent className="space-y-4 p-8">
                    <div className="flex items-start gap-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#d4af37]/30 bg-[#e5d3b3]/10 text-[#e5d3b3]">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#b99d6b]" data-testid={`legal-document-kicker-${doc.slug}`}>Grundlagentext</p>
                        <h2 className="mt-2 font-serif text-3xl text-white" data-testid={`legal-document-title-${doc.slug}`}>{doc.title}</h2>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-testid={`legal-document-flag-${doc.slug}`}>
                      <span className="h-[3px] w-8 rounded-full bg-[#2f2f2f]" />
                      <span className="h-[3px] w-8 rounded-full bg-[#a8171b]" />
                      <span className="h-[3px] w-8 rounded-full bg-[#d4af37]" />
                    </div>
                    <p className="whitespace-pre-line text-sm leading-8 text-[#ded6ca]" data-testid={`legal-document-content-${doc.slug}`}>
                      {doc.content}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </div>
      </div>
    </MainLayout>
  );
}
